import { useTranslation } from "react-i18next";
import React from "react";
import { Tooltip } from "antd";
import type { BrandComparisonData } from "@/services/mock";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { formatPercent } from "@/utils/format";
import type { BrandRatesItem } from "@/services/api/BrandRates";

interface SentimentComparisonChartProps {
  brandComparison?: BrandComparisonData | null;
  brandRatesList?: BrandRatesItem[];
  height?: number;
  title?: string;
  onDownload?: () => void;
}

const SentimentComparisonChart: React.FC<SentimentComparisonChartProps> = ({
  brandComparison,
  brandRatesList,
  height,
  title,
  onDownload,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("search_detail.sentiment_ranking");
  // 使用自定义Hook管理beta状态
  const { isBeta } = useBetaStatus();
  // 计算竞品数据
  const competitorRows = React.useMemo(() => {
    // 优先使用 brandRatesList（新接口聚合结果），回退到 brandComparison（旧结构）
    if (Array.isArray(brandRatesList) && brandRatesList.length > 0) {
      const sorted = [...brandRatesList]
        .sort((a, b) => b.positiveMentionRate - a.positiveMentionRate)
        .map((item, idx) => ({
          rank: idx + 1,
          brand: item.brandName,
          pos: Number(item.positiveMentionRate),
          neg: Number(item.negativeMentionRate),
          isCurrent: !!item.isCurBrand,
        }));
      return sorted;
    }

    if (!brandComparison?.brandList || !brandComparison?.rateList) {
      return [];
    }
    const { curBrand, otherBrands } = brandComparison.brandList;
    const { positiveMentionRate, negativeMentionRate } =
      brandComparison.rateList;
    const allBrands = [curBrand, ...otherBrands];
    const brandRates = allBrands.map((brand, index) => {
      const brandId = brand.brandId;
      const positiveRates = positiveMentionRate[brandId] || [];
      const negativeRates = negativeMentionRate[brandId] || [];
      const avgPositive =
        positiveRates.length > 0
          ? positiveRates.reduce(
              (sum, rateObj) => sum + (rateObj.rate || 0),
              0
            ) / positiveRates.length
          : 0;
      const avgNegative =
        negativeRates.length > 0
          ? negativeRates.reduce(
              (sum, rateObj) => sum + (rateObj.rate || 0),
              0
            ) / negativeRates.length
          : 0;
      return {
        rank: index + 1,
        brand: brand.brandName,
        pos: Number(avgPositive),
        neg: Number(avgNegative),
        isCurrent: index === 0,
      };
    });
    return brandRates
      .sort((a, b) => b.pos - a.pos)
      .map((item, index) => ({ ...item, rank: index + 1 }));
  }, [brandRatesList, brandComparison]);

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          fontSize: 16,
          fontWeight: 400,
          color: "rgb(148, 163, 184)",
          marginBottom: 12,
        }}
      >
        {effectiveTitle}
      </div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th
                style={{
                  padding: "10px 0",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#374151",
                  borderBottom: "1px solid #e5e7eb",
                  borderRight: "1px solid #e5e7eb",
                  width: "15%",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("home_components.chart_rank")}
              </th>
              <th
                style={{
                  padding: 0,
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#374151",
                  borderBottom: "1px solid #e5e7eb",
                  borderRight: "1px solid #e5e7eb",
                  width: "39%",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("common.charts.mention_compare_competitor")}
              </th>
              <th
                style={{
                  padding: 0,
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#374151",
                  borderBottom: "1px solid #e5e7eb",
                  borderRight: "1px solid #e5e7eb",
                  width: "23%",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("common.charts.Positive_sentiment_rate")}%
              </th>
              <th
                style={{
                  padding: 0,
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#374151",
                  borderBottom: "1px solid #e5e7eb",
                  borderRight: "1px solid #e5e7eb",
                  width: "23%",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("common.charts.Negative_sentiment_rate")}%
              </th>
            </tr>
          </thead>
          <tbody>
            {competitorRows.map((r, index) => (
              <tr
                key={r.rank}
                style={{
                  background: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                  textAlign: "center",
                  borderBottom:
                    index < competitorRows.length - 1
                      ? "1px solid #e5e7eb"
                      : "none",
                }}
              >
                <td
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                    borderRight: "1px solid #f3f4f6",
                    color: "#111827",
                    fontSize: "14px",
                  }}
                >
                  {r.rank}
                </td>
                <td
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                    borderRight: "1px solid #f3f4f6",
                    color: "#111827",
                    fontWeight: r.isCurrent ? 600 : 400,
                    fontSize: "14px",
                  }}
                >
                  {r.brand}
                  {r.isCurrent && (
                    <span
                      style={{
                        color: "#21C4B8",
                        marginLeft: "4px",
                        fontSize: 12,
                      }}
                    >
                      {t("search_detail.competitor_mark_current")}
                    </span>
                  )}
                </td>
                <td
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                    borderRight: "1px solid #f3f4f6",
                    color: "#111827",
                    fontSize: "14px",
                  }}
                >
                  {formatPercent(r.pos, 1)}
                </td>
                <td
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                    borderRight: "1px solid #f3f4f6",
                    color: "#111827",
                    fontSize: "14px",
                  }}
                >
                  {formatPercent(r.neg, 1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 解锁覆盖层 - 只在beta版本且数据超过5条时显示，只遮盖下面的5行数据 */}
        {isBeta && competitorRows.length > 5 && (
          <div
            style={{
              position: "absolute",
              top: "279px", // 从第6行开始
              left: 0, // 从表格左边缘开始
              right: 0, // 到表格右边缘
              bottom: 0, // 到表格底部
              background: "rgba(59, 130, 246, 0.1)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              cursor: "pointer",
              zIndex: 10,
              transition: "all 0.2s ease",
            }}
            onClick={() => {
              // 执行原有的下载功能
              onDownload && onDownload();

              // 平滑滚动到 NewContactUs 组件中的表单
              setTimeout(() => {
                const el = document.getElementById("new-contact-form");
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }, 100);
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                background: "transparent",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 1024 1024" fill="white">
                <path d="M771.09045312 471.64751562L252.89970312 471.64751562c-44.7575625 0-81.03670312 36.20432813-81.03670312 80.87625001l0 343.07339062c0 44.67290625 36.27914062 80.87625 81.03670312 80.87625l518.19173438 0c44.75854688 0 81.04753125-36.20334375 81.04753125-80.87625L852.13896875 552.52376563C852.13798437 507.85184375 815.849 471.64751562 771.09045312 471.64751562zM546.12434375 721.73979687l0 90.04964063c0 18.8409375-15.26864062 34.13123438-34.12926563 34.13123438-18.84979688 0-34.12926563-15.29029687-34.12926562-34.13123438l0-90.04964062c-20.37951562-11.804625-34.13025-33.80835938-34.13025-59.06348438 0-37.7015625 30.55795313-68.2605 68.25951562-68.2605s68.27034375 30.5589375 68.27034375 68.2605C580.26542188 687.9314375 566.52551562 709.93517188 546.12434375 721.73979687zM511.99507812 147.37175c-131.95251562 0-238.92946875 105.46889063-238.92946875 244.04034375l1e-8 105.98273437 477.87075 22.75284375L750.93635938 391.41209375C750.93635938 252.84064062 643.95940625 147.37175 511.99507812 147.37175zM341.36745312 491.406875L341.36745312 389.03876562c0-94.2204375 76.395375-170.627625 170.62664063-170.627625 94.24209375 0 170.627625 76.4071875 170.627625 170.627625l0 104.76309375L341.36745312 491.406875z" />
              </svg>
            </div>
            <span style={{ color: "#374151", fontSize: 14, fontWeight: 500 }}>
              {t("search_detail.competitor_join_beta")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SentimentComparisonChart;
